/** @typedef {{ key: string, label: string, format?: 'text'|'currency'|'date'|'enum' }} FieldDef */

const ENUM_LABELS = {
  maxMora: {
    UP_TO_DATE: "Al día",
    IN_ARREARS: "En mora",
    THIRTY: "Mora 30 días",
    SIXTY: "Mora 60 días",
    NINETY: "Mora 90 días o más",
  },
  viabilityStatus: {
    PENDING: "Pendiente",
    CONSULTED: "Consultada",
    APPROVED: "Aprobada",
    PRE_APPROVED: "Preaprobada",
    REJECTED: "Rechazada",
    REJECTED_DATA_VALIDATION: "Rechazada por Tus Datos",
    REJECTED_IDENTITY_VALIDATION: "Rechazada por identidad",
    REJECTED_FRAUD_VALIDATION: "Rechazada por fraude",
  },
  houseType: {
    PROPIA: "Propia",
    ARRENDADA: "Arrendada",
    FAMILIAR: "Familiar",
  },
};

export const COMMERCE_FIELDS = [
  { key: "commerceName", label: "Nombre del comercio" },
  { key: "cellphoneCommerce", label: "Celular comercio" },
  { key: "startDate", label: "Fecha de inicio", format: "date" },
  { key: "startTime", label: "Hora de inicio" },
];

export const CLIENT_ROOT_FIELDS = [
  { key: "documentNumberClient", label: "Documento" },
  { key: "nameClient", label: "Nombre" },
];

export const CLIENT_NESTED_FIELDS = [
  { key: "nameClient", label: "Nombres" },
  { key: "lastNameClient", label: "Apellidos" },
  { key: "dateExpeditionDocument", label: "Fecha expedición documento", format: "date" },
  { key: "cellphone", label: "Celular" },
  { key: "occupation", label: "Ocupación" },
  { key: "sectorOccupation", label: "Sector ocupacional" },
  { key: "descriptionOccupation", label: "Descripción ocupación" },
  { key: "incomes", label: "Ingresos", format: "currency" },
  { key: "averageCosts", label: "Gastos promedio", format: "currency" },
  { key: "dependents", label: "Dependientes" },
  { key: "extraIncomes", label: "Ingresos extra", format: "currency" },
  { key: "houseType", label: "Tipo de vivienda", format: "enum", enumGroup: "houseType" },
  { key: "rentalValue", label: "Valor arriendo", format: "currency" },
  { key: "cityAddress", label: "Ciudad" },
  { key: "placeBirth", label: "Lugar de nacimiento" },
  { key: "birth", label: "Año de nacimiento" },
  { key: "adressClient", label: "Dirección" },
];

export const VIABILITY_FIELDS = [
  { key: "incomesExperianEstimated", label: "Ingresos estimados Experian", format: "currency" },
  { key: "totalQuotas", label: "Total cuotas", format: "currency" },
  { key: "creditsClosedFinanceCoop", label: "Créditos cerrados financiero y cooperativa" },
  { key: "creditsClosedReal", label: "Créditos cerrados real" },
  { key: "creditsClosedTelco", label: "Créditos cerrados telco" },
  { key: "valueCredits", label: "Valor créditos", format: "currency" },
  { key: "maxMora", label: "Mora máxima", format: "enum", enumGroup: "maxMora" },
  { key: "status", label: "Estado de la viabilidad", format: "enum", enumGroup: "viabilityStatus" },
  { key: "rejectionReason", label: "Motivo del rechazo" },
  { key: "monthlyCapability", label: "Capacidad mensual", format: "currency" },
  { key: "incomesEstimated", label: "Ingreso estimado (declarado)", format: "currency" },
  { key: "minimumIncome", label: "Ingreso mínimo", format: "currency" },
  { key: "reportedExpenses", label: "Gastos reportados", format: "currency" },
  { key: "paymentIndicator", label: "Indicador de pago" },
  { key: "utilizationPercentage", label: "Porcentaje de utilización" },
  { key: "creditsOpenFinance", label: "Créditos abiertos financiero" },
  { key: "creditsOpenCoop", label: "Créditos abiertos cooperativa" },
  { key: "creditsOpenReal", label: "Créditos abiertos real" },
  { key: "creditsOpenTelco", label: "Créditos abiertos telco" },
  { key: "maxApprovedValue", label: "Valor máximo aprobado", format: "currency" },
];

/** Labels for the request (conversation) status shown next to the current step. */
const CONVERSATION_STATUS_LABELS = {
  ACTIVE: "En curso",
  COMPLETED: "Completada",
  REJECTED: "Rechazada",
  CANCELLED: "Cancelada",
  ABANDONED: "Abandonada",
  EXPIRED: "Vencida",
  SUPERSEDED: "Reemplazada",
  SEGMENTATION: "Sin segmento",
  STEP_NOT_FOUND: "Paso no encontrado",
};

/** @param {string|null|undefined} status */
export const formatConversationStatus = (status) => {
  if (status == null || status === "") return null;
  if (CONVERSATION_STATUS_LABELS[status]) return CONVERSATION_STATUS_LABELS[status];
  if (status.startsWith("WAITING_")) return "En espera";
  return status;
};

export const CREDIT_FIELDS = [
  { key: "creditValue", label: "Valor del crédito", format: "currency" },
  { key: "quotasNumber", label: "Número de cuotas" },
  { key: "quotaValue", label: "Valor de la cuota", format: "currency" },
];

export const formatCurrency = (value) => {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

export const formatDate = (value) => {
  if (value == null || value === "") return "—";
  try {
    const str = String(value).trim();
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(str);
    const d = match
      ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
      : new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const formatEnum = (value, group) => {
  if (value == null || value === "") return "—";
  const map = ENUM_LABELS[group];
  if (!map) return String(value);
  return map[String(value)] ?? String(value);
};

/** @param {unknown} value @param {FieldDef} field */
export const formatFieldValue = (value, field) => {
  if (value == null || value === "") return "—";
  switch (field.format) {
    case "currency":
      return formatCurrency(value);
    case "date":
      return formatDate(value);
    case "enum":
      return formatEnum(value, field.enumGroup);
    default:
      return String(value);
  }
};

/** @param {Record<string, unknown>|null|undefined} obj @param {FieldDef[]} fields */
export const isSectionEmpty = (obj, fields) => {
  if (!obj || typeof obj !== "object") return true;
  return fields.every((f) => {
    const v = obj[f.key];
    return v == null || String(v).trim() === "";
  });
};

/** @param {Array<{ sortOrder?: number }>|null|undefined} steps */
export const sortSteps = (steps) => {
  if (!Array.isArray(steps)) return [];
  return [...steps].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
};

/** @param {Array<{ stepUuid?: string }>} steps @param {string|undefined} stepUuid */
export const getActiveStepIndex = (steps, stepUuid) => {
  if (!stepUuid || !Array.isArray(steps)) return -1;
  return steps.findIndex((s) => s.stepUuid === stepUuid);
};

/** @param {Record<string, unknown>|null|undefined} root @param {FieldDef[]} rootFields @param {Record<string, unknown>|null|undefined} nested @param {FieldDef[]} nestedFields */
export const buildClientFieldValues = (root, rootFields, nested, nestedFields) => {
  const items = [];
  rootFields.forEach((f) => {
    items.push({ label: f.label, value: formatFieldValue(root?.[f.key], f) });
  });
  if (nested && typeof nested === "object") {
    nestedFields.forEach((f) => {
      items.push({ label: f.label, value: formatFieldValue(nested[f.key], f) });
    });
  }
  return items;
};

/** @param {Record<string, unknown>|null|undefined} obj @param {FieldDef[]} fields */
export const buildSectionFieldValues = (obj, fields) =>
  fields.map((f) => ({
    label: f.label,
    value: formatFieldValue(obj?.[f.key], f),
  }));
